import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaBars,
  FaBell,
  FaCheck,
  FaClock,
  FaCopy,
  FaDiscord,
  FaEdit,
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaPlay,
  FaPlayCircle,
  FaSmile,
  FaStar,
} from "react-icons/fa";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import InviteFriends from "../components/InviteFriends";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useAuth } from "../context/AuthContext";
import { finishExamOnLogout } from "../context/examService";
import { useReminders } from "../context/RemindersContext";
import { formatCustomDate } from "../utils/formatCustomDate";
import { getCourseLabel } from "../utils/getCourseLabel";

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openInviteLinkModal, setOpenInviteLinkModal] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [showHowToVideo, setShowHowToVideo] = useState(false);
  const { sessionsReminders, eventsReminders, fetchReminders } = useReminders();

  const [stats, setStats] = useState({
    events: 0,
    sessions: 0,
    invitations: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleOpenInviteLinkModal = () => {
    setOpenInviteLinkModal(true);
  };

  const handleCopyRefCode = () => {
    const textarea = document.createElement("textarea");
    textarea.value = user.referral_code;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed", err);
    }
    document.body.removeChild(textarea);
  };

  const handleLogout = async () => {
    const courseId = localStorage.getItem("current_exam_course_id");
    const token = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user"))?.token : null;

    if (courseId && token) {
      await finishExamOnLogout(courseId, token);
    }
    localStorage.removeItem("user");
    localStorage.clear();
    setUser(null);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/logout`,
        {},
        {
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
    } catch (err) {
      if (err.response?.status === 401) {
        // Unauthorized: remove user and redirect to login
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to load events. Please try again later.");
        console.error(err);
      }
    } finally {
      logout();
      localStorage.clear();
      navigate("/auth/landing", { replace: true });
    }
  };

  useEffect(() => {
    fetchReminders();
  }, [user, i18n.language]);

  // Refresh user data on every mount so admin changes (e.g. trainer status) are reflected immediately
  useEffect(() => {
    if (!user?.token) return;
    const refreshUser = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/auth/me`, {
          headers: { lang: i18n.language, Authorization: `Bearer ${user.token}` },
        });
        const fresh = { ...user, ...res?.data?.data };
        localStorage.setItem("user", JSON.stringify(fresh));
        setUser(fresh);
      } catch (err) {
        if (err.response?.status === 401) {
          setUser(null);
          navigate("/auth/login", { replace: true });
        }
      }
    };
    refreshUser();
  }, []);

  // fetch stats whenever type changes
  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/user-stats`, {
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user?.token}`,
          },
        });

        // assuming the API returns { events: 3, sessions: 5, invitations: 2 }
        setStats(response?.data?.data || {});
      } catch (err) {
        if (err.response?.status === 401) {
          setUser(null);
          navigate("/auth/login", { replace: true });
        } else {
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user?.token, i18n.language]);

  return (
    <div
      className={`flex h-screen overflow-hidden bg-white ${i18n.language === "ar" ? "rtl" : "ltr"}`}
      dir={i18n.language === "ar" ? "rtl" : "ltr"}
    >
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {/* Sidebar */}
      {!location.pathname.startsWith("/assessment") &&
        location.pathname != "/meeting" &&
        location.pathname != "/course" &&
        !location.pathname.startsWith("/course-overview") &&
        location.pathname != "/final-assessment" && (
          <aside
            className={`fixed inset-y-0 z-40 flex w-4/5 max-w-xs transform flex-col border-purple-400 bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-2xl transition-transform duration-300 ease-in-out lg:static lg:w-80 lg:translate-x-0 ltr:border-r-4 rtl:border-l-4 ${
              i18n.language === "ar"
                ? sidebarOpen
                  ? "right-0 translate-x-0"
                  : "right-0 translate-x-full"
                : sidebarOpen
                  ? "left-0 translate-x-0"
                  : "left-0 -translate-x-full"
            } `}
            dir={i18n.language === "ar" ? "rtl" : "ltr"}
          >
            {/* Logo & Greeting */}
            <div className="flex h-16 items-center justify-between gap-4 border-b border-white/20 px-4 py-2">
              <button
                onClick={() => navigate("/profile")}
                className="flex flex-col items-end gap-1 text-sm font-semibold hover:text-purple-50 focus:outline-none"
              >
                <div className="flex items-center gap-2">
                  <FaEdit className="text-lg opacity-90" />
                  <span className="text-sm opacity-90">{t("personalProfile")},</span>
                  <span className="font-bold capitalize">{user?.first_name || "User"}</span>
                </div>
              </button>
              {user?.is_trainer === 2 && (
                <span className="flex items-center gap-1 rounded-full bg-yellow-300 px-3 py-1 text-xs font-bold text-purple-800 shadow">
                  <FaStar size={11} />
                  {i18n.language === "ar" ? "مدرب" : "Trainer"}
                </span>
              )}
            </div>

            {/* Main Scrollable Content */}
            <div className="custom-scrollbar flex flex-1 flex-col space-y-1 overflow-y-auto px-3 py-2">
              {/* Reminders */}
              {(sessionsReminders.length > 0 || eventsReminders.length > 0) && (
                <div className="flex-1 space-y-2">
                  {sessionsReminders.length > 0 && (
                    <h3 className="flex gap-1">
                      <span className="text-sm font-semibold uppercase text-white">
                        {t("supportSessions")}
                      </span>
                    </h3>
                  )}
                  <div className="space-y-1">
                    {sessionsReminders.map((rem) => (
                      <SidebarCard
                        key={`session-${rem.id}`}
                        id={rem.details.id}
                        course_id={rem.details?.course_id}
                        meeting_id={rem.details?.meeting_id}
                        coaches={rem.details?.coaches}
                        time={`${formatCustomDate(rem.details.start_date)} ${rem.details.start_time}-${rem.details.end_time}`}
                        mainAction="Join"
                        actionLink={rem.details.link}
                        reminder={rem.details.is_reminder}
                        type={2}
                        token={user?.token}
                        lang={i18n.language}
                        t={t}
                        i18n={i18n}
                        fetchReminders={fetchReminders}
                        compact
                      />
                    ))}
                    {eventsReminders.length > 0 && (
                      <h3 className="flex gap-1">
                        <span className="text-sm font-semibold uppercase text-white">{t("events")}</span>
                      </h3>
                    )}
                    {eventsReminders.map((rem) => (
                      <SidebarCard
                        key={`event-${rem.id}`}
                        id={rem.details.id}
                        course_id={rem.details.course_id}
                        coaches={rem.details.coaches}
                        time={`${formatCustomDate(rem.details.start_date)} ${rem.details.start_time}-${rem.details.end_time}`}
                        mainAction="Location"
                        actionLink={rem.details.location}
                        reminder={rem.details.is_reminder}
                        type={1}
                        token={user?.token}
                        lang={i18n.language}
                        t={t}
                        i18n={i18n}
                        fetchReminders={fetchReminders}
                        isOnline={rem?.details?.is_online}
                        compact
                      />
                    ))}
                  </div>
                </div>
              )}

              {!loading && !error && sessionsReminders.length === 0 && eventsReminders.length === 0 && (
                <div className="flex flex-1 flex-col items-center justify-center gap-2 py-6 text-center">
                  <div className="flex items-center gap-1">
                    <span className="text-base">
                      <FaBell />
                    </span>
                    <span className="text-sm font-semibold text-white">{t("Noremindersyet")} </span>
                  </div>
                  <span className="text-xs text-gray-300">{t("reminderDescription")}</span>
                </div>
              )}

              {/* Progress + Actions */}
              <div className="space-y-3">
                <div className="space-y-1">
                  <p className="text-xs text-gray-300">{t("trackParticipation")}</p>
                  <ProgressCard label={t("eventsAttended")} value={stats.events} t={t} compact />
                  <ProgressCard label={t("sessionsAttended")} value={stats.sessions} t={t} compact />
                </div>
                {/* <div className="py-0">
                  <button
                    onClick={() => setShowHowToVideo(true)}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-white bg-opacity-20 py-1.5 text-sm font-semibold text-white transition hover:bg-white/40"
                  >
                    {t("howToRegister")} <FaPlayCircle />
                  </button>
                </div> */}
                {/* Invite Friends */}
                <div className="flex flex-col items-center rounded-xl bg-slate-800/70 p-3 text-center text-white shadow-md">
                  <div className="flex flex-col items-center gap-0">
                    <p className="text-2xl font-bold">{stats.invitations}</p>
                    <p className="text-xs">{t("friendsInvited")}</p>
                  </div>

                  {/* Copy Invite */}
                  <div className="mt-2 flex w-full items-center gap-2">
                    <button
                      onClick={handleOpenInviteLinkModal}
                      className="flex-1 rounded-lg bg-white/15 px-3 py-1 text-xs font-semibold text-white hover:bg-white/30"
                    >
                      {t("copyInviteLink")}
                    </button>
                    <p className="text-xs">{t("or")}</p>
                    <button
                      onClick={handleCopyRefCode}
                      className="relative flex flex-1 items-center justify-center gap-1 rounded-lg bg-white/15 px-3 py-1 text-xs font-semibold text-white hover:bg-white/30"
                    >
                      {user?.referral_code} {copied ? <FaCheck className="text-green-400" /> : <FaCopy />}
                      {/* Tooltip */}
                      {copied && (
                        <span className="absolute -top-6 left-1/2 w-16 -translate-x-1/2 rounded bg-black/80 px-2 py-1 text-xs text-white shadow-lg">
                          {t("copied")}
                        </span>
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {/* Join JoCodes Community */}
                  <div className="flex flex-col items-center rounded-xl bg-slate-800/70 p-4 text-center text-white shadow-md">
                    <div className="flex flex-1 flex-col items-center gap-2">
                      <h3 className="text-xs font-semibold">{t("joinJoCodesCommunity")}</h3>
                    </div>
                    <a
                      href="https://discord.gg/4MU9pKUGGN"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 rounded-lg bg-indigo-600 px-3 py-1 text-xs font-medium hover:bg-indigo-700"
                    >
                      <div className="flex items-center gap-2">
                        <FaDiscord className="text-white/90" size={20} />
                        <span>{t("discord")}</span>
                      </div>
                    </a>
                  </div>

                  {/* Join Surveys */}
                  <div className="flex flex-col items-center rounded-xl bg-slate-800/70 p-4 text-center text-white shadow-md">
                    <div className="flex flex-1 flex-col items-center gap-2">
                      <h3 className="text-xs font-semibold">{t("Shareyourfeedback&helpusimprove")}</h3>
                    </div>
                    <button
                      onClick={() => navigate("/surveys")}
                      className="mt-2 rounded-lg bg-indigo-600 px-3 py-1 text-xs font-medium hover:bg-indigo-700"
                    >
                      <div className="flex items-center gap-2">
                        <FaSmile className="text-white/90" size={17} />
                        <span>{t("participate")}</span>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Social */}
                <div className="space-y-2 pb-4 text-center">
                  <h3 className="text-sm font-semibold text-white">{t("followUs")}</h3>
                  <div className="flex flex-wrap justify-center gap-2">
                    <IconCircle
                      icon={<FaFacebook />}
                      link="https://www.facebook.com/profile.php?id=61565955193088"
                    />
                    <IconCircle
                      image="/twitter.png" // path to your X (Twitter) image
                      link="https://x.com/Jor_Codes"
                      alt="X (Twitter)"
                    />
                    <IconCircle
                      icon={<FaInstagram />}
                      link="https://www.instagram.com/jor_codes?igsh=cjRkcjJ0N2IyZnd1"
                    />
                    <IconCircle
                      icon={<FaLinkedin />}
                      link="https://www.linkedin.com/company/jocodes-مؤسسة-مبرمجو-الأردن"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Logout */}
            <div className="border-t border-white/20 px-4 py-[17px]">
              <button
                className="w-full rounded-full border border-white px-4 py-2 text-sm text-white transition hover:bg-white hover:text-purple-500"
                onClick={handleLogout}
              >
                {t("logout")}
              </button>
            </div>
          </aside>
        )}
      {/* Main Content */}
      <div className="flex flex-1 flex-col">
        {/* Mobile Top Bar */}
        {!location.pathname.startsWith("/assessment") &&
          location.pathname != "/meeting" &&
          location.pathname != "/course" &&
          !location.pathname.startsWith("/course-overview") &&
          location.pathname != "/final-assessment" && (
            <header className="flex h-14 items-center justify-between bg-white px-4 shadow lg:hidden">
              <button
                className="rounded-lg p-2 hover:bg-gray-100 ltr:mr-10 rtl:ml-10"
                onClick={() => setSidebarOpen(true)}
              >
                <FaBars className="text-purple-500" />
              </button>
              <h1
                onClick={() => navigate("/profile")}
                className="text-sm font-semibold capitalize text-purple-500 sm:text-base"
              >
                {t("personalProfile")}, {user?.first_name || "User"}
              </h1>
              <div className="flex items-center gap-2">
                {/* <LanguageSwitcher defaultTheme /> */}
                <img
                  onClick={() => navigate("/")}
                  src="/logo-blue.svg"
                  alt="Logo"
                  className="h-10 w-10 sm:h-12 sm:w-12"
                />
              </div>
            </header>
          )}

        {/* Main Body */}
        <main
          className={`flex flex-1 flex-col overflow-y-auto bg-white ${
            !location.pathname.startsWith("/assessment") &&
            location.pathname !== "/meeting" &&
            location.pathname != "/course" &&
            !location.pathname.startsWith("/course-overview") &&
            location.pathname != "/final-assessment"
              ? "pt-4 sm:pt-8"
              : "pt-0"
          }`}
        >
          {/* Main Content */}
          <div className="flex-1">
            <Outlet />
          </div>
          {!location.pathname.startsWith("/assessment") &&
            location.pathname != "/course" &&
            !location.pathname.startsWith("/course-overview") &&
            location.pathname != "/final-assessment" && (
              <footer dir="ltr" className="mt-auto border-t border-gray-200 bg-gray-100 py-1">
                <div className="flex flex-wrap items-center justify-center gap-8 py-2">
                  <a href="https://modee.gov.jo" target="_blank" rel="noopener noreferrer">
                    <img src="/Modee2.png" className="h-14 sm:h-[60px]" />
                  </a>
                  <a href="https://cpf.jo" target="_blank" rel="noopener noreferrer">
                    <img src="/CPF2.png" className="h-14 sm:h-16" />
                  </a>
                  <a href="https://ytj.jo" target="_blank" rel="noopener noreferrer">
                    <img src="/YTJ2.png" className="h-14 sm:h-16" />
                  </a>
                </div>
                <div className="">
                  <p className="text-center text-sm text-gray-600">
                    &copy; {new Date().getFullYear()} Designed and developed by HWK
                  </p>
                </div>
              </footer>
            )}
        </main>
      </div>
      <InviteFriends
        user={user}
        inviteLink={inviteLink}
        openInviteLinkModal={openInviteLinkModal}
        setOpenInviteLinkModal={setOpenInviteLinkModal}
      />
      {/* Video Modal */}
      {showHowToVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="relative w-full max-w-5xl rounded-2xl bg-black shadow-xl">
            <button
              onClick={() => setShowHowToVideo(false)}
              className="absolute right-3 top-3 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black"
            >
              {t("close")}
            </button>

            <div className="relative w-full overflow-hidden rounded-2xl pt-[56.25%]">
              {/* 16:9 */}
              <video
                src="https://d3fe2eyqcfkw6t.cloudfront.net/hellogate/RegistrationTutorial.mp4"
                controls
                controlsList="nodownload"
                className="absolute inset-0 h-full w-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sidebar Card

const SidebarCard = ({
  id,
  course_id,
  coaches,
  meeting_id,
  time,
  mainAction,
  actionLink,
  t,
  i18n,
  startDate,
  endDate,
  isOnline,
}) => {
  const navigate = useNavigate();

  // Convert start and end dates to Date objects
  const start = new Date(startDate);
  const end = new Date(endDate);
  const now = new Date();

  const thresholdStart = new Date(start.getTime() - 15 * 60 * 1000);

  // Check if "Join" button should be shown
  const showJoinButton = now >= thresholdStart && now <= end;

  const handleJoin = () => {
    if (actionLink) {
      navigate(
        `/meeting?link=${encodeURIComponent(actionLink)}&meetingID=${meeting_id}&sessionID=${id}&type=${mainAction !== "Join" ? "1" : "2"}`
      );
    }
  };

  return (
    <div className="group relative flex flex-col rounded-xl bg-purple-400 p-2 px-3 shadow-md backdrop-blur-md transition">
      {/* Title */}
      <div className="mb-1 flex items-start justify-between">
        <p className="break-words text-sm font-semibold text-white sm:text-base">
          {getCourseLabel(course_id, i18n.language)}
        </p>
        {/* Action Button (Bottom Right) */}
        {actionLink && (
          <div className="flex justify-end">
            {showJoinButton ? (
              <button
                onClick={handleJoin}
                className="rounded-full bg-white/30 px-3 py-0.5 text-sm font-medium text-white transition hover:bg-white hover:text-purple-500 sm:text-sm"
              >
                {t(mainAction)}
              </button>
            ) : mainAction !== "Join" ? (
              <>
                {isOnline ? (
                  showJoinButton && (
                    <button
                      onClick={handleJoin}
                      className="rounded-full bg-white/30 px-3 py-0.5 text-sm font-medium text-white transition hover:bg-white hover:text-purple-500 sm:text-sm"
                    >
                      {t("Join")}
                    </button>
                  )
                ) : (
                  <a
                    href={actionLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full bg-white/30 px-3 py-0.5 text-sm font-medium text-white transition hover:bg-white hover:text-purple-500 sm:text-sm"
                  >
                    {t(mainAction)}
                  </a>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Time */}
      <div className="mb-1 flex items-start gap-1 text-sm text-gray-200">
        <FaClock className="mt-[3.2px] text-white/70" />
        <span className="">{time}</span>
      </div>
      <div className="flex items-center">
        {/* <span className="h-1.5 w-1.5 rounded-full bg-white/80"></span> */}
        {coaches?.map((item, index) => (
          <span key={index} className="text-xs italic text-purple-100 sm:text-sm">
            {index > 0 && <span className="mx-1 text-purple-100">/</span>}
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

// Progress Card
const ProgressCard = ({ label, value, valueLabel, t }) => (
  <div className="flex justify-between rounded-full bg-white bg-opacity-20 px-3 py-2 text-sm text-white shadow backdrop-blur-md sm:px-4 sm:text-sm">
    <span className="font-medium">{t(label)}</span>
    <span className="font-semibold">
      {value} {t(valueLabel)}
    </span>
  </div>
);

// Icon Circle
const IconCircle = ({ icon, image, link, alt = "icon" }) => {
  const content = (
    <div className="flex h-8 w-8 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-white bg-opacity-30 text-white transition-transform hover:scale-110">
      {image ? <img src={image} alt={alt} className="h-3.5 w-3.5 object-contain" /> : icon}
    </div>
  );

  return link ? (
    <a href={link} target="_blank" rel="noopener noreferrer" className="inline-block">
      {content}
    </a>
  ) : (
    content
  );
};

export default DashboardLayout;
